import { useCallback, useEffect, useState } from 'react'

import { customers, type Customer, type CustomerPage } from '../api/endpoints'
import { messageFor } from '../lib/errors'

const BLANK_FORM = { code: '', name: '', country: 'IN', currency: 'USD' }

/**
 * The `customers` module's screen — and the pattern every later module copies.
 *
 * Load state is three explicit variables (loading / error / data) rather than a single
 * union, because the UI genuinely renders all three at once: a stale table with a fresh
 * error above it is more useful than a blank page.
 */
export function CustomersPage() {
  const [page, setPage] = useState<CustomerPage | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')

  const [form, setForm] = useState(BLANK_FORM)
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setPage(await customers.list({ search: search || undefined }))
    } catch (caught) {
      setError(messageFor(caught))
    } finally {
      setLoading(false)
    }
  }, [search])

  useEffect(() => {
    void load()
  }, [load])

  const handleCreate = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setFormError(null)
    setSaving(true)
    try {
      await customers.create(form)
      setForm(BLANK_FORM)
      await load()
    } catch (caught) {
      setFormError(messageFor(caught))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (customer: Customer) => {
    // Hard delete, and there is no undo — so ask first.
    if (!window.confirm(`Delete ${customer.name}? This cannot be undone.`)) return
    setError(null)
    try {
      await customers.remove(customer.id)
      await load()
    } catch (caught) {
      setError(messageFor(caught))
    }
  }

  return (
    <section className="stack">
      <header className="page-header">
        <h1>Customers</h1>
        <p className="muted">
          The hub record. Quotes, bookings, shipments and invoices will all hang off one.
        </p>
      </header>

      <form
        className="toolbar"
        onSubmit={(event) => {
          event.preventDefault()
          setSearch(searchInput.trim())
        }}
      >
        <input
          type="search"
          placeholder="Search by name or code…"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
        />
        <button type="submit" className="btn">
          Search
        </button>
        {search && (
          <button
            type="button"
            className="btn btn-quiet"
            onClick={() => {
              setSearchInput('')
              setSearch('')
            }}
          >
            Clear
          </button>
        )}
      </form>

      {error && (
        <p className="alert alert-error" role="alert">
          {error}
        </p>
      )}

      <div className="card">
        <h2>
          {page ? `${page.total} customer${page.total === 1 ? '' : 's'}` : 'Customers'}
          {search && <span className="muted"> matching “{search}”</span>}
        </h2>

        {loading && !page ? (
          <p className="muted">Loading…</p>
        ) : page && page.items.length > 0 ? (
          <table className="table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Country</th>
                <th>Currency</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {page.items.map((customer) => (
                <tr key={customer.id}>
                  <td className="mono">{customer.code}</td>
                  <td>{customer.name}</td>
                  <td>{customer.country}</td>
                  <td>{customer.currency}</td>
                  <td>
                    {customer.is_active ? (
                      <span className="pill pill-ok">active</span>
                    ) : (
                      <span className="pill">inactive</span>
                    )}
                  </td>
                  <td className="right">
                    <button
                      type="button"
                      className="btn btn-quiet btn-danger"
                      onClick={() => void handleDelete(customer)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted">
            {search ? 'No customers match that search.' : 'No customers yet.'}
          </p>
        )}
      </div>

      <form className="card" onSubmit={handleCreate}>
        <h2>Add a customer</h2>

        <div className="form-row">
          <label className="field">
            <span>Code</span>
            <input
              required
              maxLength={32}
              placeholder="IWL-0042"
              value={form.code}
              onChange={(event) => setForm({ ...form, code: event.target.value })}
            />
          </label>

          <label className="field">
            <span>Name</span>
            <input
              required
              maxLength={200}
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          </label>

          <label className="field field-narrow">
            <span>Country</span>
            <input
              required
              maxLength={2}
              value={form.country}
              onChange={(event) => setForm({ ...form, country: event.target.value.toUpperCase() })}
            />
          </label>

          <label className="field field-narrow">
            <span>Currency</span>
            <input
              required
              maxLength={3}
              value={form.currency}
              onChange={(event) => setForm({ ...form, currency: event.target.value.toUpperCase() })}
            />
          </label>
        </div>

        {formError && (
          <p className="alert alert-error" role="alert">
            {formError}
          </p>
        )}

        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Add customer'}
        </button>
      </form>
    </section>
  )
}
